import { getAccount } from '@/api';
import type {
  EventMarketDto,
  EventOddDto,
  EventRowExtendedDto,
  MainMarketDefinition,
} from '@/api/queries';
import { parseApiUtcDate } from '@/lib/date';
import { getTournamentOrCategoryBadge, sportIcon, toSlug } from '@/shell/sports-menu-utils';
import type { OddsFlash } from '@/ui/common/odds-button';
import type {
  DesktopEventMarket,
  DesktopEventRowProps,
} from '@/ui/sports/DesktopEventRow';
import type {
  EventCompetitor,
  EventOutcome,
  EventRowIndicator,
  EventRowProps,
} from '@/ui/sports/EventRow';
import type { EventWidgetProps } from '@/ui/sports/EventWidget';
import { isLiveStreamConfigured } from './lmt';
import { mergeOddsPatches, outcomeFlashKey } from './live-odds';
import {
  applyLiveStatisticsPatch,
  resolveEventRowLiveInfo,
  type LiveEventStatisticsPatch,
} from './live-statistics';
import { formatOddForDisplay } from './odds-format';

export function parseMarketsString(marketsString: string | null | undefined): EventMarketDto[] | null {
  if (!marketsString) return null;
  try {
    const parsed: unknown = JSON.parse(marketsString);
    return Array.isArray(parsed) ? (parsed as EventMarketDto[]) : null;
  } catch {
    return null;
  }
}

function emptyOdds(header: MainMarketDefinition): EventOddDto[] {
  return header.DefaultOutcomes.map((outcome) => ({
    Outcome: outcome,
    Value: 0,
    Date: '',
    Active: true,
  }));
}

function emptyMarket(header: MainMarketDefinition): EventMarketDto {
  return {
    Id: header.MarketId,
    Key: header.Key,
    Group: header.Key,
    Active: true,
    Lines: [{ Specifier: '', Active: true, Odds: emptyOdds(header) }],
  };
}

function ensureLineOdds(market: EventMarketDto, header: MainMarketDefinition): EventMarketDto {
  const lines = market.Lines?.length
    ? [...market.Lines]
    : [{ Specifier: '', Active: false, Odds: [] as EventOddDto[] }];

  const firstLine = lines[0] ?? { Specifier: '', Active: false, Odds: [] as EventOddDto[] };
  const line = { ...firstLine, Odds: [...(firstLine.Odds ?? [])] };

  if (line.Odds.length === 0) {
    line.Odds = emptyOdds(header);
  } else if (line.Odds.length < header.DefaultOutcomes.length) {
    line.Odds = header.DefaultOutcomes.map(
      (outcome) =>
        line.Odds.find((odd) => odd.Outcome === outcome) ?? {
          Outcome: outcome,
          Value: 0,
          Date: '',
          Active: true,
        },
    );
  }

  return { ...market, Lines: [line, ...lines.slice(1)] };
}

type MarketsInput = string | EventMarketDto[] | null | undefined;

function toMarkets(input: MarketsInput): EventMarketDto[] | null {
  return typeof input === 'string' || input == null ? parseMarketsString(input) : input;
}

function alignMarketToHeader(
  header: MainMarketDefinition,
  markets: EventMarketDto[] | null,
): EventMarketDto {
  const unsorted = markets;
  if (!unsorted) return emptyMarket(header);

  const match = unsorted.find(
    (market) =>
      market.Key === header.Key || (header.Key === 'NXG' && market.Group === header.Key),
  );

  return match ? ensureLineOdds(match, header) : emptyMarket(header);
}

/**
 * Resolve which list header drives the mobile row: selected key (any header,
 * including Additional), else first non-additional, else first header.
 */
export function resolveMainMarketHeader(
  marketHeaders: MainMarketDefinition[] | undefined,
  selectedMarketKey?: string,
): MainMarketDefinition | null {
  if (!marketHeaders?.length) return null;
  if (selectedMarketKey) {
    const selected = marketHeaders.find((header) => header.Key === selectedMarketKey);
    if (selected) return selected;
  }
  return marketHeaders.find((header) => !header.Additional) ?? marketHeaders[0] ?? null;
}

/**
 * Simplified port of legacy `SortEventMarkets` for EventRow: return the main
 * market aligned to its header (optionally overridden by `selectedMarketKey`).
 */
export function pickMainMarket(
  marketHeaders: MainMarketDefinition[] | undefined,
  marketsInput: MarketsInput,
  selectedMarketKey?: string,
): EventMarketDto | null {
  const header = resolveMainMarketHeader(marketHeaders, selectedMarketKey);
  if (!header) return null;
  return alignMarketToHeader(header, toMarkets(marketsInput));
}

/** All non-additional main markets aligned to their headers (desktop multi-column odds). */
export function pickMainMarkets(
  marketHeaders: MainMarketDefinition[] | undefined,
  marketsInput: MarketsInput,
): { header: MainMarketDefinition; market: EventMarketDto }[] {
  const headers = marketHeaders?.filter((header) => !header.Additional) ?? [];
  const markets = toMarkets(marketsInput);
  return headers.map((header) => ({
    header,
    market: alignMarketToHeader(header, markets),
  }));
}

/**
 * Desktop row markets: primary (non-additional) first, then additional headers
 * so wide viewports can fill extra columns.
 */
export function pickDesktopMarkets(
  marketHeaders: MainMarketDefinition[] | undefined,
  marketsInput: MarketsInput,
): { header: MainMarketDefinition; market: EventMarketDto }[] {
  if (!marketHeaders?.length) return [];
  const primary = marketHeaders.filter((header) => !header.Additional);
  const extra = marketHeaders.filter((header) => header.Additional);
  const markets = toMarkets(marketsInput);
  return [...primary, ...extra].map((header) => ({
    header,
    market: alignMarketToHeader(header, markets),
  }));
}

function pickMainLine(market: EventMarketDto) {
  if (!market.Lines?.length) return null;
  return market.Lines.find((line) => line.Main === true) ?? market.Lines[0];
}

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const dayMonthFormatter = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
});

function formatKickoff(dateValue: string): string {
  const date = parseApiUtcDate(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = timeFormatter.format(date);

  if (sameDay) return `Today, ${time}`;

  return `${dayMonthFormatter.format(date)}, ${time}`;
}

/** Desktop stacked kickoff: `DD/MM` + `HH:mm`. */
export function formatKickoffParts(
  dateValue: string,
): { date: string; time: string } | undefined {
  const date = parseApiUtcDate(dateValue);
  if (Number.isNaN(date.getTime())) return undefined;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return { date: `${day}/${month}`, time: timeFormatter.format(date) };
}

export type EventRowMapOptions = {
  /** When true, format as live even if `event.Live` is false (live tables feed). */
  treatAsLive?: boolean;
  /** WebsiteTranslates lookup for live period codes (`HalfTime`, `2HT`, …). */
  translatePeriod?: (key: string) => string;
  /** Socket-driven statistics override for this row. */
  statisticsPatch?: LiveEventStatisticsPatch;
  /** Socket-driven odds overlay for this row. */
  oddsPatch?: EventMarketDto[];
  /** Lookup active odds flash direction by outcome flash key. */
  getOutcomeFlash?: (flashKey: string) => OddsFlash | undefined;
  /** Socket-driven event metadata (active, markets count). */
  eventMetaPatch?: { active?: boolean; marketsCount?: number; expireOdds?: string };
  /** When true and the event offers BB, surface the list badge. */
  betBuilderEnabled?: boolean;
  /** Mobile list filter: which MarketHeaders Key every row should show. */
  selectedMarketKey?: string;
};

/** REST snapshot markets with the socket overlay merged in, parsed once. */
function resolveMarkets(
  event: EventRowExtendedDto,
  options?: EventRowMapOptions,
): EventMarketDto[] | null {
  const base = parseMarketsString(event.MarketsString);
  if (!options?.oddsPatch?.length) return base;
  return mergeOddsPatches(base ?? [], options.oddsPatch);
}

function isLiveContext(event: EventRowExtendedDto, options?: EventRowMapOptions): boolean {
  return options?.treatAsLive === true || event.Live;
}

function coerceScore(value: unknown): number | null | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

function formatScore(value: unknown): string | undefined {
  const numeric = coerceScore(value);
  if (numeric === null || numeric === undefined || Number.isNaN(numeric)) return undefined;
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
}

/**
 * PeriodScore is sometimes a single string with home/away halves separated by `|`
 * (e.g. `22 30|21 28`). When that shape is missing, leave period scores unset.
 */
function splitPeriodScores(periodScore: string | null | undefined): [string?, string?] {
  if (!periodScore?.trim()) return [];
  const parts = periodScore.split('|').map((part) => part.trim());
  if (parts.length >= 2) return [parts[0] || undefined, parts[1] || undefined];
  return [];
}

function marketOutcomes(
  market: EventMarketDto | null,
  header: MainMarketDefinition | undefined,
  eventActive: boolean,
  flashContext?: {
    eventId: number;
    marketKey: string;
    getOutcomeFlash?: (flashKey: string) => OddsFlash | undefined;
  },
): EventOutcome[] {
  if (!market) {
    return (header?.DefaultOutcomes ?? []).map((outcome) => ({
      id: outcome,
      outcome,
      odds: '—',
      state: 'locked' as const,
    }));
  }

  const line = pickMainLine(market);
  const odds = line?.Odds ?? [];
  const specifier = header?.HasSpecifier ? line?.Specifier?.trim() || undefined : undefined;
  // Prefer DefaultOutcomes length — those are the OutcomeTranslates keys (UOF: 1/2/3).
  // Outcomes are display hints and may be glyphs like `+`/`-` for totals — never use
  // those as translate keys or visible labels; append `specifier` after translate instead.
  const count = Math.max(
    header?.DefaultOutcomes?.length ?? 0,
    header?.Outcomes?.length ?? 0,
    odds.length,
  );

  return Array.from({ length: count }, (_, index) => {
    const code = header?.DefaultOutcomes[index] ?? odds[index]?.Outcome ?? String(index);
    const odd = odds.find((item) => item.Outcome === code) ?? odds[index];
    const value = odd?.Value ?? 0;
    const locked = !eventActive || !odd || !odd.Active || value <= 0;
    const flashKey =
      flashContext?.getOutcomeFlash && flashContext.eventId
        ? outcomeFlashKey(flashContext.eventId, flashContext.marketKey, specifier, code)
        : undefined;
    const flash = flashKey ? flashContext?.getOutcomeFlash?.(flashKey) : undefined;

    return {
      id: `${market.Key}:${odd?.Outcome ?? code}`,
      outcome: code,
      outcomeCode: code,
      odds: formatOddForDisplay(value),
      state: locked ? ('locked' as const) : ('default' as const),
      specifier,
      flash,
      marketId: market.Key,
      marketName: header?.Key ?? market.Key,
      oddValue: value > 0 ? value : undefined,
    };
  });
}

export type MappedEventRowProps = Omit<
  EventRowProps,
  'onOpenEvent' | 'onSelectOutcome' | 'onShowStats' | 'onToggleFavorite'
>;

export function mapEventToRowProps(
  event: EventRowExtendedDto,
  marketHeaders: MainMarketDefinition[] | undefined,
  options?: EventRowMapOptions,
): MappedEventRowProps {
  return buildRowProps(event, marketHeaders, options, resolveMarkets(event, options));
}

function buildRowProps(
  event: EventRowExtendedDto,
  marketHeaders: MainMarketDefinition[] | undefined,
  options: EventRowMapOptions | undefined,
  markets: EventMarketDto[] | null,
): MappedEventRowProps {
  const live = isLiveContext(event, options);
  const mainHeader = resolveMainMarketHeader(marketHeaders, options?.selectedMarketKey);
  const mainMarket = pickMainMarket(marketHeaders, markets, options?.selectedMarketKey);
  const patchedEvent = live
    ? applyLiveStatisticsPatch(event, options?.statisticsPatch)
    : event;
  const liveInfo = live
    ? resolveEventRowLiveInfo(patchedEvent, options?.translatePeriod)
    : null;
  const [homePeriods, awayPeriods] = splitPeriodScores(event.PeriodScore);
  const homeScore = liveInfo?.homeScore ?? formatScore(event.HomeScore);
  const awayScore = liveInfo?.awayScore ?? formatScore(event.AwayScore);
  const homeGameScores = liveInfo?.homeGameScores ?? homePeriods;
  const awayGameScores = liveInfo?.awayGameScores ?? awayPeriods;
  const indicators: EventRowIndicator[] =
    event.LiveStream && isLiveStreamConfigured(getAccount()) ? ['livestream'] : [];
  const eventActive =
    (options?.eventMetaPatch?.active ?? event.Active) && !event.Blocked;
  const flashContext = {
    eventId: event.EventId,
    marketKey: mainHeader?.Key ?? mainMarket?.Key ?? '',
    getOutcomeFlash: options?.getOutcomeFlash,
  };

  const competitors: [EventCompetitor, EventCompetitor] = [
    {
      name: event.Home ?? 'Home',
      score: live ? homeScore : undefined,
      periodScores: live ? homeGameScores : homePeriods,
      setScore: live ? liveInfo?.homeSetScore : undefined,
      serving: live ? liveInfo?.currentServer === 0 : undefined,
    },
    {
      name: event.Away ?? 'Away',
      score: live ? awayScore : undefined,
      periodScores: live ? awayGameScores : awayPeriods,
      setScore: live ? liveInfo?.awaySetScore : undefined,
      serving: live ? liveInfo?.currentServer === 1 : undefined,
    },
  ];

  const crestSrc = getTournamentOrCategoryBadge(event.Tournament, event.Category);

  return {
    league: [event.Category, event.Tournament].filter(Boolean).join(' > '),
    category: event.Category ?? undefined,
    categoryId: event.CategoryId,
    tournament: event.Tournament ?? undefined,
    tournamentId: event.TournamentId ?? undefined,
    competitors,
    marketCode: mainHeader?.Key ?? mainMarket?.Key ?? '',
    outcomes: marketOutcomes(mainMarket, mainHeader ?? undefined, eventActive, flashContext),
    type: live ? 'live' : 'prematch',
    state: !eventActive ? 'suspended' : 'default',
    kickoff: live ? undefined : formatKickoff(event.Date),
    clock: live ? liveInfo?.clock : undefined,
    liveClockState: live ? liveInfo?.liveClockState : undefined,
    crestSrc,
    indicators: indicators.length > 0 ? indicators : undefined,
    additionalMarkets: options?.eventMetaPatch?.marketsCount ?? event.MarketsCount,
    betBuilderAvailable:
      options?.betBuilderEnabled === true &&
      !live &&
      event.BetBuilderAvailable === true,
  };
}

/** Homepage EventWidget mapping — comma league, sport icon, scores always shown as slots. */
export function mapEventToWidgetProps(
  event: EventRowExtendedDto,
  marketHeaders: MainMarketDefinition[] | undefined,
): Omit<EventWidgetProps, 'onOpenEvent' | 'onSelectOutcome'> {
  const row = mapEventToRowProps(event, marketHeaders);
  const homeScore = formatScore(event.HomeScore);
  const awayScore = formatScore(event.AwayScore);

  return {
    league: [event.Category, event.Tournament].filter(Boolean).join(', '),
    category: event.Category ?? undefined,
    categoryId: event.CategoryId,
    tournament: event.Tournament ?? undefined,
    tournamentId: event.TournamentId ?? undefined,
    sportIcon: sportIcon(toSlug(event.Sport ?? '')),
    competitors: [
      { name: row.competitors[0].name, score: event.Live ? homeScore : undefined },
      { name: row.competitors[1].name, score: event.Live ? awayScore : undefined },
    ],
    marketCode: row.marketCode,
    outcomes: row.outcomes,
    type: row.type,
    state: row.state,
    kickoff: row.kickoff,
    clock: row.clock,
  };
}

/** DesktopEventRow mapping — all non-additional markets as odds columns. */
export function mapEventToDesktopRowProps(
  event: EventRowExtendedDto,
  marketHeaders: MainMarketDefinition[] | undefined,
  options?: EventRowMapOptions,
): Omit<
  DesktopEventRowProps,
  'onOpenEvent' | 'onSelectOutcome' | 'onShowStats' | 'onToggleFavorite'
> {
  const live = isLiveContext(event, options);
  const resolvedMarkets = resolveMarkets(event, options);
  const row = buildRowProps(event, marketHeaders, options, resolvedMarkets);
  const aligned = pickDesktopMarkets(marketHeaders, resolvedMarkets);
  const eventActive =
    (options?.eventMetaPatch?.active ?? event.Active) && !event.Blocked;
  const flashResolver = options?.getOutcomeFlash;

  const markets: DesktopEventMarket[] = aligned.map(({ header, market }) => ({
    code: header.Key,
    outcomes: marketOutcomes(market, header, eventActive, {
      eventId: event.EventId,
      marketKey: header.Key,
      getOutcomeFlash: flashResolver,
    }),
  }));

  const kickoffParts = live ? undefined : formatKickoffParts(event.Date);

  return {
    league: row.league,
    category: row.category,
    categoryId: row.categoryId,
    tournament: row.tournament,
    tournamentId: row.tournamentId,
    competitors: row.competitors,
    markets: markets.length > 0 ? markets : [{ code: row.marketCode, outcomes: row.outcomes }],
    type: row.type,
    state: row.state,
    kickoffDate: kickoffParts?.date,
    kickoffTime: kickoffParts?.time,
    clock: row.clock,
    liveClockState: row.liveClockState,
    crestSrc: row.crestSrc,
    indicators: row.indicators,
    additionalMarkets: row.additionalMarkets,
    betBuilderAvailable: row.betBuilderAvailable,
  };
}
